import { bucketCatalog } from './bucketCatalog'
import { MIN_TARGET_SPEND_CENTS } from './bucket.types'
import type {
  BucketComposition,
  BucketLine,
  BucketProduct,
  ComposeInput,
  TasteId,
} from './bucket.types'

const familyDemoOrder = [
  'puff-clusters',
  'rainbow-treats',
  'pick-mix-200',
  'fudge',
] as const

const tasteOrder: readonly TasteId[] = [
  'freeze-dried',
  'fruity',
  'sour',
  'chocolate',
  'caramel',
]

const familyDemoRank = new Map<string, number>(
  familyDemoOrder.map((id, index) => [id, index]),
)

interface Candidate {
  readonly product: BucketProduct
  readonly matchingTaste: TasteId | undefined
  readonly catalogIndex: number
}

function candidateOrder(left: Candidate, right: Candidate): number {
  const leftIsWildcard = left.matchingTaste === undefined
  const rightIsWildcard = right.matchingTaste === undefined

  if (leftIsWildcard !== rightIsWildcard) {
    return leftIsWildcard ? 1 : -1
  }

  const leftRank = familyDemoRank.get(left.product.id)
  const rightRank = familyDemoRank.get(right.product.id)

  if (leftRank !== undefined || rightRank !== undefined) {
    if (leftRank === undefined) return 1
    if (rightRank === undefined) return -1
    return leftRank - rightRank
  }

  return left.catalogIndex - right.catalogIndex
}

function eligibleCandidates(input: ComposeInput): Candidate[] {
  const hardNos = new Set(input.hardNos)
  const likes = new Set(input.likes)
  const candidates: Candidate[] = []

  bucketCatalog.forEach((product, catalogIndex) => {
    if (product.tags.some((tag) => hardNos.has(tag))) return

    const matchingTaste = product.tags.find((tag) => likes.has(tag))
    if (matchingTaste === undefined && input.adventure === 'safe') return

    candidates.push({ product, matchingTaste, catalogIndex })
  })

  return candidates.sort(candidateOrder)
}

function canonicalTasteKey(tastes: readonly TasteId[]): string {
  const selectedTastes = new Set(tastes)
  const canonicalTastes = tasteOrder
    .filter((taste) => selectedTastes.has(taste))
    .join('.')

  return canonicalTastes || 'none'
}

function compositionId(
  input: ComposeInput,
  lines: readonly BucketLine[],
): string {
  const lineKey =
    lines
      .map(
        (line) =>
          `${line.productId}.${line.priceCents}.${line.quantity}`,
      )
      .join(',') || 'none'

  return [
    'demo',
    input.mode,
    input.targetSpendCents,
    input.adventure,
    `likes_${canonicalTasteKey(input.likes)}`,
    `hard-nos_${canonicalTasteKey(input.hardNos)}`,
    `lines_${lineKey}`,
  ].join('-')
}

function assertValidTargetSpend(targetSpendCents: number): void {
  if (
    !Number.isSafeInteger(targetSpendCents) ||
    targetSpendCents < MIN_TARGET_SPEND_CENTS
  ) {
    throw new RangeError(
      `targetSpendCents must be a finite integer of at least ${MIN_TARGET_SPEND_CENTS}`,
    )
  }
}

export function composeBucket(input: ComposeInput): BucketComposition {
  assertValidTargetSpend(input.targetSpendCents)

  const lines: BucketLine[] = []
  let subtotalCents = 0
  let wildcardCount = 0
  const maxWildcards =
    input.adventure === 'safe'
      ? 0
      : input.adventure === 'one_wildcard'
        ? 1
        : Number.POSITIVE_INFINITY

  for (const candidate of eligibleCandidates(input)) {
    if (lines.length === 4) break

    const isWildcard = candidate.matchingTaste === undefined
    if (isWildcard && wildcardCount >= maxWildcards) continue

    const nextSubtotalCents = subtotalCents + candidate.product.priceCents

    if (
      lines.length >= 2 &&
      nextSubtotalCents > input.targetSpendCents + 300
    ) {
      continue
    }

    lines.push({
      productId: candidate.product.id,
      name: candidate.product.name,
      image: candidate.product.image,
      priceCents: candidate.product.priceCents,
      quantity: 1,
      reason:
        candidate.matchingTaste === undefined
          ? 'A wildcard outside your selected tastes.'
          : `Matches your ${candidate.matchingTaste} taste.`,
    })
    subtotalCents = nextSubtotalCents
    if (isWildcard) wildcardCount += 1
  }

  return {
    id: compositionId(input, lines),
    name:
      input.mode === 'family'
        ? 'The Family Freeze-Up'
        : 'Your Treat Show Bucket',
    lines,
    subtotalCents,
    status: lines.length === 0 ? 'no_match' : 'ready',
    inventoryStatus: 'demo_unverified',
  }
}
